<template>
  <dialog
    id="modal"
    ref="dialog"
    class="fixed inset-0 z-50 w-screen h-screen bg-black dark:bg-gray-700 backdrop-filter dark:backdrop-blur-sm dark:bg-opacity-50 bg-opacity-50 flex justify-center items-center transition-all"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="'modal-title'"
    :aria-describedby="'modalComponent'"
  >
    <div
      class="modalContainer bg-white dark:border dark:border-gray-500/60 w-full h-full rounded shadow relative transition-all"
      :class="isMobile ? 'lg:w-5/6 lg:h-5/6' : 'md:w-3/4 md:h-3/4'"
    >
      <div class="bg-secondary dark:bg-secondary-600/60 rounded-t h-10 flex justify-between sticky top-0 dark:border-b dark:border-gray-500/40">
        <div class="flex items-center relative overflow-hidden">
          <Transition name="back-btn">
            <button
              v-if="hasPreviousModal"
              @click="returnToPreviousModal"
              ref="returnModalBtn"
              title="Back"
              aria-label="Go back"
              class="font-bold rounded flex items-center text-white p-2 ml-2 h-8"
            >
              <font-awesome-icon icon="fa-solid fa-arrow-left" class="h-5 w-5" />
            </button>
          </Transition>

          <h3
            id="modal-title"
            class="text-white font-bold mx-auto px-4 truncate text-left uppercase"
          >
            {{ modalHeaderTitle }}
          </h3>
        </div>
        <button
          @click="closeModal"
          ref="closeBtn"
          title="Close"
          aria-label="Close"
          class="font-bold mr-2 rounded flex items-center"
        >
          <font-awesome-icon icon="fa-regular fa-xmark" class="h-8 w-8 text-white rounded hover:bg-primary-50 hover:bg-opacity-10" />
        </button>
      </div>
      <div
        id="modalComponent"
        aria-live="polite"
        class="relative overflow-hidden"
        :class="hasPreviousModal ? 'h-modal' : 'h-modal'"
      >
        <!--Current modal component. :class="currentModal.component" is needed to target specific modals in the dom-->
        <Transition
          :name="modalTransitionName"
          @before-leave="handleBeforeLeave"
          @before-enter="handleBeforeEnter"
        >
          <KeepAlive :key="keepAliveKey">
            <component
              v-if="currentModal"
              :is="registeredComponent(currentModal.component)"
              :key="currentModal.id"
              :class="[ modalClasses(currentModal.component)]"
              v-bind="currentModal.args"
              ref="currentModal"
              tabindex="-1"
              data-scroll-container
              :modal-index="currentModalIndex"
              :active-tab="activeTab[currentModalIndex]"
              :data-modal-id="currentModal.id"
              @set-active-tab="setActiveTab"
            />
          </KeepAlive>
        </Transition>
      </div>
    </div>
  </dialog>
</template>

<script>
import { mapGetters, mapMutations } from "vuex"
import { defineAsyncComponent } from "vue"

// Grab every Vue file anywhere in /Frontend that ends with *Modal.vue
// The ** makes it recursive
const modules = import.meta.glob([
  "/**/*Modal.vue",              // include all modals
])

// Remove VModal.vue explicitly
const filteredModules = Object.fromEntries(
  Object.entries(modules).filter(([path]) => !path.endsWith("/VModal.vue"))
)

// Build a registry: { "AvatarModal": defineAsyncComponent(...) }
const modalRegistry = Object.fromEntries(
  Object.entries(filteredModules).map(([path, loader]) => {
    // Extract filename without extension (e.g. AvatarModal.vue → AvatarModal)
    const name = path.split("/").pop().replace(".vue", "")
    return [
      name,
      defineAsyncComponent({
        loader,
        loadingComponent: LoadingSpinnerModalAsync,
      })
    ]
  })
)
import LoadingSpinnerModalAsync from "../Loading/LoadingSpinner/LoadingSpinnerModalAsync.vue"

export default {
  name: "VModal",
  components: {
    LoadingSpinnerModalAsync
  },
  data() {
    return {
      previousActiveElement: null,
      keepAliveKey: 0,
      visibleIdx: 0,
      activeTab: {}
    }
  },
  computed: {
    ...mapGetters([
      "modalHistory",
      "currentModal",
      "hasPreviousModal",
      "modalOpen",
      "modalArgs",
      "currentModalIndex",
      "getModalHeaderTitle",
      "getModalClosePrevent",
      "displayModalClosePrompt",
      "actionOnModalCloseProceed",
      "modalTransitionName",
      "modalDirection",
      "getPromptText",
      "isPromptProceed",
      "getProceedAction",
    ]),
    currentModalComponent() {
      return this.currentModal?.component
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    modalHeaderTitle() {
      return this.getModalHeaderTitle
    },
    navigationMode() {
      return this.currentModal.mode
    },
    URLparams() {
      return this.$route.query
    },
  },
  watch: {
    // show prompt if prevented closing
    displayModalClosePrompt(val, oldVal) {
      if (val && val !== oldVal && this.getModalClosePrevent) {
        this.setPrompt(this.getModalClosePrevent)
      }
    },
    // proceed with modal action on prompt proceed
    async isPromptProceed(val) {
      if (val === true && this.getProceedAction === "proceedWithModalClose") {
        await this.preventModalClose(null)
        await this[this.actionOnModalCloseProceed]()
        this.setPrompt([null, null])
      }
    },
    currentModal(val, oldVal) {
      if (val?.mode === "reset") {
        this.keepAliveKey++
      }
      else {

        // threshold modals have their own nav logic, skip _submodal_ for them
        const modalNoSubmodals = [
          "ChemThresholdGroupsModal",
          "ChemThresholdModal"
        ]
        if (modalNoSubmodals.includes(this.currentModalComponent)) return

        // console.log("new modal", val)
        // console.log(this.modalDirection)
        if (val !== oldVal) {
          if (this.modalDirection === "forward") {
            this.visibleIdx++
            this.$router.push({
              query: {
                ...this.$route.query,
                [`_submodal_${this.visibleIdx}`]: this.modalHistory[this.visibleIdx].component,
                [`_submodal_${this.visibleIdx}_tab`]: val.args.modalTab || 0
              }
            })
            if (val.args.modalTab !== 0) this.activeTab[this.visibleIdx] = val.args.modalTab
          }
          else if (this.modalDirection === "back") {
            const query = { ...this.$route.query }
            delete query[`_submodal_${this.visibleIdx}`]
            delete query[`_submodal_${this.visibleIdx}_tab`]

            this.$router.push({ query })
            this.visibleIdx--
          }
        }
      }
    },
    // for router navigation, BACK and FORWARD arrows
    async URLparams(val, oldVal) {

      const cameFromPop =
        this.$store.state.lastPopNavigationPosition ===
        window.history.state?.position;

      // Avoid duplicate calls if params were not changed through arrows
      if (!cameFromPop) {
        return;
      }

      // threshold modals have their own nav logic, skip _submodal_ for them
        const modalNoSubmodals = [
          "EditTankModal",
          "ChemThresholdGroupsModal",
          "ChemThresholdModal"
        ]
      if (modalNoSubmodals.includes(this.currentModalComponent)) return

      if (val !== oldVal) {
        if (!isNaN(val._modal_tab) && val._modal_tab !== oldVal._modal_tab) {
          this.activeTab[0] = Number(val._modal_tab)
        }
        if (!isNaN(val[`_submodal_${this.currentModalIndex}_tab`]) && val[`_submodal_${this.currentModalIndex}_tab`] !== oldVal[`_submodal_${this.currentModalIndex}_tab`]) {
          this.activeTab[this.currentModalIndex] = Number(val[`_submodal_${this.currentModalIndex}_tab`])
        }
        if (oldVal[`_submodal_${this.currentModalIndex}`] && !val[`_submodal_${this.currentModalIndex}`]) {
          console.log(`removed _submodal_${this.currentModalIndex}`)
          this.returnToPreviousModal()
        }
        if (val[`_submodal_${this.currentModalIndex+1}`]) {
          try {
            this.setActiveModal({
              component: this.modalHistory[this.currentModalIndex + 1].component,
              args: this.modalHistory[this.currentModalIndex + 1].args
            })
          }
          catch {
            console.log("can't go to component")
            this.$router.replace({ query: oldVal })
          }
        }
      }
    }
  },
  beforeCreate() {
    this.previousActiveElement = document.activeElement
    document.querySelector("body").style.overflow = "hidden"
  },
  async mounted() {
    // on mount, remove submodals if coming from browser back button
    const hasSubmodals = Object.keys(this.$route.query)
    .some(key => key.startsWith('_submodal_'))

    if (hasSubmodals) {
      const cleanedQuery = { ...this.$route.query }
      Object.keys(cleanedQuery).forEach(key => {
        if (key.startsWith('_submodal_')) {
          delete cleanedQuery[key]
        }
      })
      this.$router.replace({ query: cleanedQuery })
    }
    document.getElementById("cruz-app")?.setAttribute("aria-hidden", "true")
    this.setTabsFromQuery()
    // this.$refs.dialog?.showModal()
    document.addEventListener("focusin", this.handleTab, true)
    document.addEventListener("keydown", this.handleKeyDown, true)
    await this.$nextTick()
    this.$refs.closeBtn.focus()
  },
  beforeUnmount() {
    document.getElementById("cruz-app")?.removeAttribute("aria-hidden")
    document.querySelector("body").style.overflow = "auto"
    document.removeEventListener("focusin", this.handleTab, true)
    document.removeEventListener("keydown", this.handleKeyDown, true)
    if (this.previousActiveElement) {
      this.previousActiveElement.focus()
    }
  },
  methods: {
    ...mapMutations([
      "closeModal",
      "setActiveModal",
      "setModalScroll",
      "amendModalArgs",
      "returnToPreviousModal",
      "removePreviousModals",
      "preventModalClose",
      "toggleModalClosePrompt",
      "setPrompt"
    ]),
    // Resolves the actual Vue component from the registry
    registeredComponent(modalComponent) {
      return modalComponent
        ? modalRegistry[modalComponent] || null
        : null
    },
    modalClasses(modalComponent) {
      let classes = [ 'w-full', 'h-full', 'overflow-y-auto', 'fadein', modalComponent ]
      classes.push(this.hasPreviousModal ? "h-full" : "h-full")
      return classes
    },
    handleTab(e) {
      if (!this.$refs.currentModal || this.getPromptText || !e.sourceCapabilities) return
      const tabbedInsideModal = e.target === this.$refs.currentModal
        || this.$refs.currentModal.$el.contains(e.target)
        || e.target === this.$refs.returnModalBtn
      if (!tabbedInsideModal) {
        // redirect to close button
        this.$refs.closeBtn.focus()
      }
    },
    handleKeyDown(e) {
      if (e.key === "Escape") {
        this.closeModal()
        return
      }
    },
    handleBeforeLeave(el) {
      // el = DOM element of the modal being left
      const scrollContainer = el
      if (!scrollContainer) return
      const modalId = el.dataset.modalId
      if (!modalId) return
      
      this.setModalScroll({
        id: modalId,
        scrollTop: scrollContainer.scrollTop
      })
    },
    handleBeforeEnter(el) {
      const modalId = el.dataset.modalId
      if (!modalId) return

      const modal = this.modalHistory.find(m => m.id === modalId)
      if (!modal) return

      const scrollContainer = el
      if (!scrollContainer) return

      requestAnimationFrame(() => {
        scrollContainer.scrollTop = modal.scrollTop || 0
      })
    },
    async setTabsFromQuery() {
      setTimeout(() => {
        if (!isNaN(this.URLparams?._modal_tab) && this.currentModalIndex == 0) {
          this.activeTab[0] = Number(this.URLparams?._modal_tab)
        }
        else if (!isNaN(this.modalArgs?.modalTab)) {
          this.setActiveTab(this.modalArgs?.modalTab)
        }
      }, 1)

      // save submodal indexes on reload
      // TODO: reload submodals on load
      // const pattern = /^_submodal_\d+_tab$/

      // const matches = Object.entries(this.$route.query)
      //   .filter(([key]) => pattern.test(key))
      // console.log(matches)
      // for (let submodalIdxMatch of matches) {
      //   this.activeTab[submodalIdxMatch] = Number(this.URLparams?.[`_submodal_${submodalIdxMatch}_tab`])
      // }
    },
    async setActiveTab(payload) {

      const data = typeof payload === "number"
        ? { idx: payload }
        : payload

      const idx = data.idx

      this.activeTab[this.currentModalIndex] = idx

      setTimeout(() => {
        let updatedQuery = {
          ...this.$route.query,
          [
            this.currentModalIndex == 0
            ? "_modal_tab"
            : `_submodal_${this.currentModalIndex}_tab`
          ]: idx
        }
        if (data.queryDelete) {
          for (let param of data.queryDelete) {
            delete updatedQuery[param]
          }
        }
        this.$router.push({ query: updatedQuery })
        // console.log("setting active tab for ", this.currentModalComponent, idx)
      }, 1)

      this.amendModalArgs({ modalTab: idx })
    },
  },
}
</script>

<style scoped>
#modal {
  height: 100%;
  height: 100dvh;
}
.h-modal {
  height: calc(100% - theme("height.10"));
}
.h-2modal {
  height: calc(100% - 2.5rem);
}
/* =========================
   FORWARD (push)
   ========================= */

.slide-forward-enter-active,
.slide-forward-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-forward-enter-from {
  transform: translateX(100%);
  opacity: 0.8;
}

.slide-forward-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.slide-forward-leave-active {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.slide-forward-leave-to {
  transform: translateX(-100%);
  opacity: 0.8;
}

/* =========================
   BACK (pop)
   ========================= */

.slide-back-enter-active,
.slide-back-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-back-enter-from {
  transform: translateX(-100%);
  opacity: 0.8;
}

.slide-back-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.slide-back-leave-active {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.slide-back-leave-to {
  transform: translateX(100%);
  opacity: 0.8;
}

.back-btn-enter-active,
.back-btn-leave-active {
  transition: all 0.25s ease;
}

/* entering */
.back-btn-enter-from {
  opacity: 0;
  transform: translateX(-10px) scale(0.9);
  width: 0;
  margin-left: 0;
  padding: 0;
}

.back-btn-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
}

/* leaving */
.back-btn-leave-to {
  opacity: 0;
  transform: translateX(-10px) scale(0.9);
  width: 0;
  margin-left: 0;
  padding: 0;
}

/* ── Modal dark mode ────────────────────────────── */
/* Backdrop overlay — heavier scrim so page doesn't bleed through */
/* [data-theme="dark"] #modal {
  /* background-color: rgba(0, 0, 0, 0.80) !important; 
} */

/* Modal header bar (bg-secondary → darkened) */
/* [data-theme="dark"] #modal .bg-secondary {
  background-color: rgba(18, 24, 48, 0.95) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
} */

[data-theme="dark"] .modalContainer {
  box-shadow: -7px -4px 33px -4px #65ceff1b;
}

/* Modal body (bg-white → dark surface) */
[data-theme="dark"] #modal>div {
  background-color: rgba(10, 14, 22) !important;
}

/* Modal internal content text */
[data-theme="dark"] #modalComponent {
  color: rgba(255, 255, 255);
}

/* Modal title headers inside content */
[data-theme="dark"] #modalComponent h1,
[data-theme="dark"] #modalComponent h2,
[data-theme="dark"] #modalComponent h3 {
  color: rgba(255, 255, 255, 0.92) !important;
}

/* Modal labels and secondary text */
[data-theme="dark"] #modalComponent label {
  color: rgba(255, 255, 255, 0.65) !important;
}

[data-theme="dark"] #modalComponent p {
  color: rgba(255, 255, 255, 0.55) !important;
}
</style>